import { Add, UserMultiple } from "@carbon/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";

import { companyMembersQueryOptions } from "@/api/companyMembers";
import { listTeamsQueryOptions } from "@/api/teams";
import { AddMembersDialog } from "@/components/admin/teams/addMembersDialog";
import { TeamDetail } from "@/components/admin/teams/teamDetail";
import { TeamDialog } from "@/components/admin/teams/teamDialog";
import { TeamList } from "@/components/admin/teams/teamList";
import { TeamsPageSkeleton } from "@/components/admin/teams/teamsPageSkeleton";
import { UnassignedNotice } from "@/components/admin/teams/unassignedNotice";
import { UnassignedReview } from "@/components/admin/teams/unassignedReview";
import { Button, buttonVariants } from "@/components/input/button";
import { Employee } from "@/schemas/employee";
import { Team } from "@/schemas/team";
import { TeamIdSchema } from "@/schemas/teamId";
import { lenient } from "@/util/lenient";
import { unassignedEmployees } from "@/util/teams";

// Teams (specs/015-teams-screen): permanent, flat company teams. The selected team, the review
// of people in no team and the search live in the URL, so reloads and shared links keep them.
const TeamsSearchSchema = z.object({
  team: lenient(z.coerce.number().pipe(TeamIdSchema)),
  view: lenient(z.enum(["unassigned"])),
  q: lenient(z.string().min(1)),
});

export const Route = createFileRoute("/dashboard/_authenticated/admin/teams")({
  component: RouteComponent,
  validateSearch: TeamsSearchSchema,
  staticData: {
    pageWidth: "wide",
    breadcrumb: ["Teams"],
  },
});

const SUBTITLE = "Organize people into teams. Need a smaller split, like Backend? Make it a group when you create the evaluation.";

type DialogState = { kind: "create" } | { kind: "edit"; team: Team } | { kind: "addMembers"; team: Team } | undefined;

function RouteComponent() {
  const teamsQuery = useQuery(listTeamsQueryOptions());
  const membersQuery = useQuery(companyMembersQueryOptions());
  const [dialog, setDialog] = useState<DialogState>();

  return (
    <div className="
      flex min-w-0 flex-col gap-6 pb-6
      lg:gap-8
    ">
      <header className="
        flex flex-col gap-4 border-b-2 border-primary-100 pb-6
        sm:flex-row sm:items-start sm:justify-between
      ">
        <div className="flex flex-col gap-1">
          <h1 className="text-page-title text-primary-700">Teams</h1>
          <p className="text-body2 text-neutral-600">{SUBTITLE}</p>
        </div>
        <Button
          className="
            min-h-11 shrink-0 justify-center
            lg:min-h-0
          "
          intent="primary"
          onClick={() => setDialog({ kind: "create" })}
          size="sm"
          type="button"
        >
          <Add aria-hidden size={16} />
          New team
        </Button>
      </header>

      <TeamsBody
        employees={membersQuery.data?.content}
        employeesError={membersQuery.isError}
        onDialog={setDialog}
        onRetryEmployees={() => void membersQuery.refetch()}
        onRetryTeams={() => void teamsQuery.refetch()}
        pending={teamsQuery.isPending || membersQuery.isPending}
        teams={teamsQuery.data}
        teamsError={teamsQuery.isError}
      />

      <TeamsDialogs dialog={dialog} employees={membersQuery.data?.content ?? []} onClose={() => setDialog(undefined)} teams={teamsQuery.data ?? []} />
    </div>
  );
}

function TeamsBody({
  teams,
  employees,
  pending,
  teamsError,
  employeesError,
  onRetryTeams,
  onRetryEmployees,
  onDialog,
}: {
  teams: Team[] | undefined;
  employees: Employee[] | undefined;
  pending: boolean;
  teamsError: boolean;
  employeesError: boolean;
  onRetryTeams: () => void;
  onRetryEmployees: () => void;
  onDialog: (dialog: DialogState) => void;
}) {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const detailRef = useRef<HTMLDivElement>(null);

  // Below `lg` the detail sits under the list: bring it into view when a team is picked.
  useEffect(() => {
    if (search.team !== undefined && !globalThis.matchMedia("(min-width: 64rem)").matches) {
      detailRef.current?.scrollIntoView({ block: "start" });
    }
  }, [search.team]);

  if (teamsError) {
    return <RetryMessage message="Couldn't load teams." onRetry={onRetryTeams} />;
  }

  if (!teams || (pending && !employeesError)) {
    return <TeamsPageSkeleton />;
  }

  // Without the members list the counts fall back to the stored uuids.
  const people = employees ?? [];
  const unassignedCount = employees ? unassignedEmployees(teams, employees).length : 0;
  const query = search.q ?? "";

  if (teams.length === 0 && search.view !== "unassigned") {
    return (
      <div className="flex flex-col gap-4">
        <div className="
          flex flex-col items-center gap-3 rounded-lg border-2
          border-primary-100 px-6 py-12 text-center
        ">
          <UserMultiple aria-hidden className="text-primary-400" size={32} />
          <h2 className="text-h3 text-primary-700">No teams yet</h2>
          <p className="max-w-md text-body2 text-neutral-600">{SUBTITLE}</p>
          <Button className="
            min-h-11
            lg:min-h-0
          " intent="primary" onClick={() => onDialog({ kind: "create" })} size="sm" type="button">
            <Add aria-hidden size={16} />
            New team
          </Button>
        </div>
        <UnassignedNotice count={unassignedCount} />
      </div>
    );
  }

  const selected = search.team === undefined ? teams[0] : teams.find((team) => team.id === search.team);

  return (
    <div className="
      grid grid-cols-1 items-start gap-8
      lg:grid-cols-[20rem_1fr]
    ">
      <TeamList
        employees={employees ?? countOnlyEmployees(teams)}
        footer={<UnassignedNotice count={unassignedCount} query={search.q} />}
        onSearch={(value) => void navigate({ search: (previous) => ({ ...previous, q: value || undefined }), replace: true })}
        query={query}
        selectedId={search.view === "unassigned" ? undefined : selected?.id}
        teams={teams}
      />

      <div className="min-w-0 scroll-mt-6" ref={detailRef}>
        <DetailColumn
          employees={people}
          employeesError={employeesError}
          onDialog={onDialog}
          onRetryEmployees={onRetryEmployees}
          selected={selected}
          teams={teams}
        />
      </div>
    </div>
  );
}

function DetailColumn({
  teams,
  employees,
  selected,
  employeesError,
  onRetryEmployees,
  onDialog,
}: {
  teams: Team[];
  employees: Employee[];
  selected: Team | undefined;
  employeesError: boolean;
  onRetryEmployees: () => void;
  onDialog: (dialog: DialogState) => void;
}) {
  const search = Route.useSearch();

  if (employeesError) {
    return <RetryMessage message="Couldn't load company members." onRetry={onRetryEmployees} />;
  }

  if (search.view === "unassigned") {
    return <UnassignedReview employees={employees} teams={teams} />;
  }

  if (!selected) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-neutral-600">Team not found.</div>
        <Link
          className="
            flex min-h-11 items-center text-body2 font-semibold
            text-primary-default
            hover:underline
            lg:min-h-0
          "
          search={{ q: search.q }}
          to="/dashboard/admin/teams"
        >
          Back to teams
        </Link>
      </div>
    );
  }

  return (
    <TeamDetail
      employees={employees}
      onAddMembers={() => onDialog({ kind: "addMembers", team: selected })}
      onEdit={() => onDialog({ kind: "edit", team: selected })}
      team={selected}
      teams={teams}
    />
  );
}

/**
 * Stand-in members when the employees request failed, so the list still shows counts: one
 * placeholder per stored uuid (search by person and names aren't available then).
 */
function countOnlyEmployees(teams: Team[]): Employee[] {
  const uuids = new Set(teams.flatMap((team) => team.employeeUuids));
  return [...uuids].map((uuid) => ({ uuid, name: "", email: "", isActive: true, createdDate: "" }));
}

function RetryMessage({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-4">
      <span className="text-body2 text-error-300">{message}</span>
      <button
        className={buttonVariants({ size: "sm", className: "min-h-11 lg:min-h-0" })}
        onClick={onRetry}
        type="button"
      >
        Retry
      </button>
    </div>
  );
}

function TeamsDialogs({
  dialog,
  teams,
  employees,
  onClose,
}: {
  dialog: DialogState;
  teams: Team[];
  employees: Employee[];
  onClose: () => void;
}) {
  const navigate = Route.useNavigate();
  const onOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };
  // The dialog's team as currently cached, so a refetch while it's open isn't lost.
  const dialogTeam = dialog && dialog.kind !== "create"
    ? (teams.find((team) => team.id === dialog.team.id) ?? dialog.team)
    : undefined;

  return (
    <>
      <TeamDialog
        employees={employees}
        onDeleted={() => void navigate({ search: (previous) => ({ ...previous, team: undefined }) })}
        onOpenChange={onOpenChange}
        onSaved={(saved) => void navigate({ search: (previous) => ({ ...previous, team: saved.id, view: undefined }) })}
        open={dialog?.kind === "create" || dialog?.kind === "edit"}
        team={dialog?.kind === "edit" ? dialogTeam : undefined}
        teams={teams}
      />
      {dialog?.kind === "addMembers" && dialogTeam && (
        <AddMembersDialog
          employees={employees}
          onOpenChange={onOpenChange}
          open
          team={dialogTeam}
          teams={teams}
        />
      )}
    </>
  );
}
